import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

type OnboardingStatus = "draft" | "kyc_pending" | "data_pending" | "review" | "active";

interface OnboardingProgressProps {
  /** Current family onboarding status */
  status: OnboardingStatus | string | null | undefined;
  className?: string;
}

const STEPS = [
  { key: "draft", label: "Organization Setup" },
  { key: "kyc_pending", label: "KYC Information" },
  { key: "data_pending", label: "Portfolio Import" },
  { key: "active", label: "Ready" },
] as const;

/** Maps each status to its 0-based step index (how many steps are completed). */
const STATUS_TO_COMPLETED: Record<OnboardingStatus, number> = {
  draft: 0,
  kyc_pending: 1,
  data_pending: 2,
  review: 3,
  active: 4,
};

export function OnboardingProgress({ status, className }: OnboardingProgressProps) {
  const normalized = (status || "draft") as OnboardingStatus;
  const completedCount = STATUS_TO_COMPLETED[normalized] ?? 0;

  return (
    <nav aria-label="Onboarding progress" className={cn("w-full", className)}>
      <ol className="flex items-center">
        {STEPS.map((step, idx) => {
          const isCompleted = idx < completedCount;
          const isCurrent = idx === completedCount && completedCount < STEPS.length;
          const isLast = idx === STEPS.length - 1;

          return (
            <li
              key={step.key}
              className={cn("flex items-center", !isLast && "flex-1")}
            >
              {/* Circle + label */}
              <div className="flex flex-col items-center gap-1.5">
                <div
                  className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-medium transition-colors",
                    isCompleted &&
                      "border-primary bg-primary text-primary-foreground",
                    isCurrent &&
                      "border-primary bg-background text-primary",
                    !isCompleted &&
                      !isCurrent &&
                      "border-muted-foreground/30 bg-background text-muted-foreground/50"
                  )}
                >
                  {isCompleted ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    idx + 1
                  )}
                </div>
                <span
                  className={cn(
                    "text-xs text-center whitespace-nowrap",
                    isCompleted && "text-primary font-medium",
                    isCurrent && "text-foreground font-medium",
                    !isCompleted && !isCurrent && "text-muted-foreground"
                  )}
                >
                  {step.label}
                </span>
              </div>

              {/* Connector line */}
              {!isLast && (
                <div
                  className={cn(
                    "mx-2 h-0.5 flex-1 transition-colors",
                    idx < completedCount
                      ? "bg-primary"
                      : "bg-muted-foreground/20"
                  )}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
